import React, { useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Form, {
  Item,
  Label,
  ButtonItem,
  ButtonOptions,
  RequiredRule,
  EmailRule,
  CustomRule,
} from "devextreme-react/form";
import notify from "devextreme/ui/notify";
import LoadIndicator from "devextreme-react/load-indicator";
// import { createAccount } from "../../api/auth";

import "./CreateAccountForm.scss";
import AUTH_API from "api/authApi";
import { usePost } from "@hooks/useAuthApi";
import Loader from "@common-components/loader/Loader";

export default function CreateAccountForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [passwordMode, setPasswordMode] = useState("password");
  const [confirmPasswordMode, setConfirmPasswordMode] = useState("password");
  const { mutate: createAccount } = usePost(
    "create-account",
    AUTH_API.REGISTER
  );
  const formData = useRef({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmedPassword: "",
    mobileNo: "",
    company: "",
  });

  const getPasswordEditorOptions = (
    passwordMode,
    setPasswordMode,
    placeholder
  ) => ({
    stylingMode: "filled",
    placeholder,
    mode: passwordMode,
    buttons: [
      {
        name: "lockIcon",
        location: "before",
        options: {
          icon: "lock",
          stylingMode: "text",
        },
      },
      {
        name: "togglePassword",
        location: "after",
        options: {
          icon: passwordMode === "password" ? "eyeopen" : "eyeclose",
          stylingMode: "text",
          onClick: () =>
            setPasswordMode((prev) =>
              prev === "password" ? "text" : "password"
            ),
        },
      },
    ],
  });

  const passwordEditorOptions = getPasswordEditorOptions(
    passwordMode,
    setPasswordMode,
    "Enter Password"
  );
  const confirmedPasswordEditorOptions = getPasswordEditorOptions(
    confirmPasswordMode,
    setConfirmPasswordMode,
    "Confirm Password"
  );

  const passwordValidation = (e) => {
    const password = e.value;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const isValidLength = password.length >= 8 && password.length <= 20;

    return (
      hasUpperCase &&
      hasLowerCase &&
      hasDigit &&
      hasSpecialChar &&
      isValidLength
    );
  };
  const mobileValidation = (e) => {
    const value = e.value;
  
    const isOnlyNumbers = /^[0-9]+$/.test(value);
    const isValidLength = value.length >= 8 && value.length <= 20;
  
    return isOnlyNumbers && isValidLength;
  };

  const confirmPassword = useCallback(
    ({ value }) => value === formData.current.password,
    []
  );

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setLoading(true);

      const {
        company,
        confirmedPassword,
        email,
        firstName,
        lastName,
        mobileNo,
        password,
      } = formData.current;
      createAccount(
        {
          firstName,
          lastName,
          email,
          password,
          mobileNo,
          userIsSystemAdmin: true,
          company,
        },
        {
          onSuccess: (response) => {
            if (response.isSuccess) {
              navigate("/login");
              notify("Account is registered!", "success", 2500);
            } else {
              notify(response.errors[0].errorMessageEn, "error", 3000);
            }
          },
          onError: (error) => {
            notify(
              // error.message || "Login failed. Please try again.",
              error.message,
              "warning",
              3000
            );
          },
          onSettled: () => {
            setLoading(false);
          },
        }
      );

      setLoading(false);

      // const result = await createAccount(formData.current);
      // if (result?.isOk) {
      //   navigate("/login");
      // } else {
      //   notify(result.message, "error", 2000);
      // }
    },
    [navigate]
  );

  return (
    <form className={"create-account-form"} onSubmit={onSubmit}>
      {/* <p className="text-xl mb-1">Welcome to QMS! 👋🏻</p>
      <p className="text-xs">Please create your account here!</p> */}
      <Form
        formData={formData.current}
        disabled={loading}
        labelMode="outside"
        labelLocation="top"
        showColonAfterLabel={false}
        className="mt-4"
        colCount={2}
      >
        <Item
          colSpan={1}
          dataField={"firstName"}
          editorType={"dxTextBox"}
          editorOptions={{ placeholder: "Enter your first name" }}
        >
          <RequiredRule message="First name is required" />
        </Item>
        <Item
          colSpan={1}
          dataField={"lastName"}
          editorType={"dxTextBox"}
          editorOptions={{ placeholder: "Enter your last name" }}
        >
          <RequiredRule message="Last name is required" />
        </Item>

        <Item
          colSpan={2}
          dataField={"email"}
          editorType={"dxTextBox"}
          editorOptions={emailEditorOptions}
        >
          <RequiredRule message="Email is required" />
          <EmailRule message="Email is invalid" />
        </Item>
        <Item
          colSpan={2}
          dataField={"password"}
          editorType={"dxTextBox"}
          editorOptions={passwordEditorOptions}
        >
          <RequiredRule message="Password is required" />
          <CustomRule
            message="Password must be 8-20 characters, include at least 1 uppercase letter, 1 lowercase letter, 1 digit, and 1 special character."
            validationCallback={passwordValidation}
          />
        </Item>
        <Item
          colSpan={2}
          dataField={"confirmedPassword"}
          editorType={"dxTextBox"}
          label={{ text: "Confirm Password" }}
          editorOptions={confirmedPasswordEditorOptions}
        >
          <RequiredRule message="Password is required" />
          <CustomRule
            message={"Passwords do not match"}
            validationCallback={confirmPassword}
          />
        </Item>
        <div className="form-row">
          <Item
            dataField={"mobileNo"}
            editorType={"dxTextBox"}
            label={{ visible: true, text: "Contact No" }}
            editorOptions={{ placeholder: "Enter contact number" }}
          >
          <CustomRule
            message={"Only digits are allowed and should be greater than 8 and less than 11"}
            validationCallback={mobileValidation}
          />
            <RequiredRule message="Mobile number is required" />
          </Item>
          <Item
            dataField={"company"}
            editorType={"dxTextBox"}
            editorOptions={{ placeholder: "Enter company name" }}
          >
            <RequiredRule message="Company name is required" />
          </Item>
        </div>
        <ButtonItem colSpan={2}>
          <ButtonOptions
            width={"100%"}
            height={"40px"}
            type={"default"}
            useSubmitBehavior={true}
          >
            <span className="dx-button-text">
              {loading ? (
                <Loader  />
              ) : (
                "Sign Up"
              )}
            </span>
          </ButtonOptions>
        </ButtonItem>
      </Form>
    </form>
  );
}

const emailEditorOptions = {
  stylingMode: "filled",
  placeholder: "Enter your email",
  mode: "email",
  buttons: [
    {
      name: "emailIcon",
      location: "before",
      options: {
        icon: "email",
        stylingMode: "text",
      },
    },
  ],
};
